import countries from './phone-countries.json';

export const PINNED_ISOS = ['DE', 'PL', 'RO', 'UA', 'TR', 'VN', 'GB'];

const BY_ISO = new Map(countries.map((c) => [c.iso, c]));

/** Dial codes longest-first for parsing +… */
const DIALS_DESC = [...new Set(countries.map((c) => c.dial))]
    .sort((a, b) => b.length - a.length);

export function getCountry(iso) {
    return BY_ISO.get(String(iso || '').toUpperCase()) || null;
}

const FUNNEL_INTL = {
    de: 'de',
    en: 'en',
    pl: 'pl',
    vi: 'vi',
    uk: 'uk',
    tr: 'tr',
    ro: 'ro',
};

const displayNamesByLang = new Map();

function intlLocale(language) {
    if (typeof language !== 'string') {
        return 'en';
    }
    const key = language.toLowerCase().replace('_', '-').split('-')[0];
    return FUNNEL_INTL[key] || 'en';
}

/**
 * Country name in the Quick Apply funnel language (browser CLDR, no CDN).
 */
export function countryDisplayName(iso, language) {
    const code = String(iso || '').toUpperCase();
    const fallback = getCountry(iso)?.name || code;
    if (!/^[A-Z]{2}$/.test(code)) {
        return fallback;
    }
    const locale = intlLocale(language);
    try {
        let dn = displayNamesByLang.get(locale);
        if (!dn) {
            if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') {
                return fallback;
            }
            dn = new Intl.DisplayNames([locale], { type: 'region' });
            displayNamesByLang.set(locale, dn);
        }
        const label = dn.of(code);
        if (!label || label === code) {
            return fallback;
        }
        return label;
    } catch (e) {
        return fallback;
    }
}

export function flagSrc(flagBaseUrl, iso) {
    const code = String(iso || '').toLowerCase();
    const base = String(flagBaseUrl || '');
    if (!base || !/^[a-z]{2}$/.test(code)) {
        return '';
    }
    return `${base}${code}.webp`;
}

/**
 * Browser locale → ISO2. Region subtag wins (en-GB → GB). Fallback DE.
 */
export function defaultIsoFromLocale(locale = '') {
    const raw = String(locale || (typeof navigator !== 'undefined' ? navigator.language : '') || '');
    const parts = raw.replace('_', '-').split('-');
    if (parts.length >= 2) {
        const region = parts[parts.length - 1].toUpperCase();
        if (BY_ISO.has(region)) {
            return region;
        }
    }
    const lang = (parts[0] || '').toLowerCase();
    const langMap = {
        de: 'DE',
        pl: 'PL',
        ro: 'RO',
        uk: 'UA',
        tr: 'TR',
        vi: 'VN',
        en: 'GB',
    };
    if (langMap[lang] && BY_ISO.has(langMap[lang])) {
        return langMap[lang];
    }
    return 'DE';
}

export function countriesForList(language = 'en') {
    const locale = intlLocale(language);
    const pinned = PINNED_ISOS.map((iso) => BY_ISO.get(iso)).filter(Boolean);
    const pinnedSet = new Set(PINNED_ISOS);
    const rest = countries
        .filter((c) => !pinnedSet.has(c.iso))
        .slice()
        .sort((a, b) => countryDisplayName(a.iso, language).localeCompare(
            countryDisplayName(b.iso, language),
            locale,
            { sensitivity: 'base' }
        ));
    return [...pinned, ...rest];
}

export function filterCountries(query, language = 'en') {
    const q = String(query || '').trim().toLowerCase().replace(/^\+/, '');
    const list = countriesForList(language);
    if (!q) {
        return list;
    }
    return list.filter((c) => {
        const localized = countryDisplayName(c.iso, language).toLowerCase();
        const english = String(c.name || '').toLowerCase();
        const dial = c.dial;
        const iso = c.iso.toLowerCase();
        return localized.includes(q)
            || english.includes(q)
            || dial.startsWith(q)
            || iso === q
            || `+${dial}`.includes(q);
    });
}

export function digitsOnly(value) {
    return String(value || '').replace(/\D+/g, '');
}

export function toE164(iso, national) {
    const country = getCountry(iso);
    if (!country) {
        return '';
    }
    let nationalDigits = digitsOnly(national);
    if (nationalDigits.startsWith(country.dial) && nationalDigits.length > country.dial.length) {
        nationalDigits = nationalDigits.slice(country.dial.length);
    }
    if (nationalDigits.startsWith('0')) {
        nationalDigits = nationalDigits.replace(/^0+/, '');
    }
    if (nationalDigits === '') {
        return '';
    }
    return `+${country.dial}${nationalDigits}`;
}

export function isCoarseE164(value) {
    return /^\+[1-9]\d{6,14}$/.test(String(value || '').trim());
}

export function isPlausibleNational(iso, national) {
    const e164 = toE164(iso, national);
    return isCoarseE164(e164);
}

/**
 * Split stored E.164 back into iso + national for the UI.
 */
export function splitE164(e164, preferredIso = '') {
    const parsed = parseInternational(e164, preferredIso);
    if (!parsed) {
        return { iso: preferredIso || defaultIsoFromLocale(), national: '' };
    }
    return parsed;
}

/**
 * Parse a pasted/typed international number. Longest dial-code match.
 * Shared codes (+1, +7, +44, …): keep preferredIso if it matches, else US / RU / GB.
 */
export function parseInternational(raw, preferredIso = '') {
    let text = String(raw || '').trim();
    if (text === '') {
        return null;
    }
    if (text.startsWith('00')) {
        text = `+${text.slice(2)}`;
    }
    if (!text.startsWith('+')) {
        return null;
    }
    const rest = digitsOnly(text.slice(1));
    if (rest === '') {
        return null;
    }
    let dial = '';
    for (const candidate of DIALS_DESC) {
        if (rest.startsWith(candidate)) {
            dial = candidate;
            break;
        }
    }
    if (!dial) {
        return null;
    }
    let national = rest.slice(dial.length);
    if (national.startsWith('0')) {
        national = national.replace(/^0+/, '');
    }
    const matches = countries.filter((c) => c.dial === dial);
    if (matches.length === 0) {
        return null;
    }
    const preferred = getCountry(preferredIso);
    let iso = matches[0].iso;
    if (preferred && preferred.dial === dial) {
        iso = preferred.iso;
    } else if (dial === '1' && BY_ISO.has('US')) {
        iso = 'US';
    } else if (dial === '7' && BY_ISO.has('RU')) {
        iso = 'RU';
    } else if (dial === '44' && BY_ISO.has('GB')) {
        iso = 'GB';
    } else {
        const pinnedHit = PINNED_ISOS.find((code) => matches.some((c) => c.iso === code));
        if (pinnedHit) {
            iso = pinnedHit;
        }
    }
    return { iso, national, dial };
}

export function isTelField(field) {
    const type = String(field?.type || '').toLowerCase();
    return type === 'tel' || type === 'phone';
}

function escapeAttr(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;');
}

function setFlagImg(img, flagBaseUrl, iso) {
    if (!img) {
        return;
    }
    const src = flagSrc(flagBaseUrl, iso);
    img.hidden = !src;
    if (src) {
        img.src = src;
        img.alt = '';
    }
}

/**
 * Wire a rendered tel wrap. Hidden input [data-qa-field] holds E.164.
 */
export function mountPhoneField(wrap, options = {}) {
    const flagBaseUrl = options.flagBaseUrl || '';
    const labels = options.labels || {};
    const funnelLanguage = typeof options.language === 'string' ? options.language : '';
    const onChange = options.onChange || null;
    const hidden = wrap.querySelector('[data-qa-field]');
    const national = wrap.querySelector('[data-qa-phone-national]');
    const trigger = wrap.querySelector('[data-qa-phone-trigger]');
    const dialEl = wrap.querySelector('[data-qa-phone-dial]');
    const flagImg = wrap.querySelector('[data-qa-phone-flag]');
    if (!hidden || !national || !trigger) {
        return () => {};
    }

    let iso = defaultIsoFromLocale();
    const initial = String(hidden.value || '').trim();
    if (initial.startsWith('+')) {
        const split = splitE164(initial, iso);
        iso = split.iso;
        national.value = split.national;
    } else if (initial !== '') {
        const parsed = parseInternational(`+${digitsOnly(initial)}`, iso);
        if (parsed) {
            iso = parsed.iso;
            national.value = parsed.national;
        } else {
            national.value = digitsOnly(initial);
        }
    }

    const syncHidden = () => {
        hidden.value = toE164(iso, national.value);
        if (typeof onChange === 'function') {
            onChange();
        }
    };

    const paintCountry = () => {
        const country = getCountry(iso) || getCountry('DE');
        iso = country ? country.iso : 'DE';
        if (dialEl) {
            dialEl.textContent = `+${getCountry(iso).dial}`;
        }
        setFlagImg(flagImg, flagBaseUrl, iso);
        trigger.setAttribute(
            'aria-label',
            `${countryDisplayName(iso, funnelLanguage)}, +${getCountry(iso).dial}`
        );
        syncHidden();
    };

    paintCountry();

    const applyParsed = (raw) => {
        const parsed = parseInternational(raw, iso);
        if (!parsed) {
            return false;
        }
        iso = parsed.iso;
        national.value = parsed.national;
        paintCountry();
        return true;
    };

    const onNationalInput = () => {
        const raw = String(national.value || '');
        if (raw.includes('+') || raw.trim().startsWith('00')) {
            applyParsed(raw);
            return;
        }
        syncHidden();
    };

    national.addEventListener('input', onNationalInput);
    national.addEventListener('paste', (e) => {
        const clip = e.clipboardData ? e.clipboardData.getData('text') : '';
        if (clip && (clip.includes('+') || clip.trim().startsWith('00'))) {
            e.preventDefault();
            applyParsed(clip);
        }
    });

    let overlay = null;
    const closeOverlay = () => {
        if (!overlay) {
            return;
        }
        overlay.remove();
        overlay = null;
    };

    const openOverlay = () => {
        closeOverlay();
        overlay = document.createElement('div');
        overlay.className = 'quick-apply__phone-overlay';
        overlay.setAttribute('data-qa-phone-overlay', '1');
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        const searchPh = escapeAttr(labels.phoneSearch || 'Search country');
        overlay.innerHTML = `<div class="quick-apply__phone-overlay-card">`
            + `<input type="search" class="form-control quick-apply__phone-search" data-qa-phone-search`
            + ` placeholder="${searchPh}" autocomplete="off" role="combobox"`
            + ` aria-expanded="true" aria-autocomplete="list" aria-controls="qa-phone-list" />`
            + `<ul class="quick-apply__phone-list" id="qa-phone-list" role="listbox" data-qa-phone-list></ul>`
            + `</div>`;
        document.body.appendChild(overlay);

        const list = overlay.querySelector('[data-qa-phone-list]');
        const search = overlay.querySelector('[data-qa-phone-search]');
        let highlighted = 0;

        const optionButtons = () => [...list.querySelectorAll('.quick-apply__phone-option')];

        const setHighlight = (index) => {
            const buttons = optionButtons();
            if (buttons.length === 0) {
                highlighted = -1;
                search.removeAttribute('aria-activedescendant');
                return;
            }
            highlighted = Math.max(0, Math.min(index, buttons.length - 1));
            buttons.forEach((btn, i) => {
                const on = i === highlighted;
                btn.classList.toggle('is-active', on);
                btn.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            const active = buttons[highlighted];
            search.setAttribute('aria-activedescendant', active.id);
            active.scrollIntoView({ block: 'nearest' });
        };

        const renderList = (query) => {
            list.innerHTML = '';
            const rows = filterCountries(query, funnelLanguage);
            rows.forEach((c) => {
                const li = document.createElement('li');
                li.setAttribute('role', 'presentation');
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.id = `qa-phone-opt-${c.iso}`;
                btn.className = 'quick-apply__phone-option';
                btn.setAttribute('role', 'option');
                btn.tabIndex = -1;
                if (c.iso === iso) {
                    btn.classList.add('is-selected');
                }
                const img = document.createElement('img');
                img.alt = '';
                img.loading = 'lazy';
                img.width = 20;
                img.height = 14;
                img.src = flagSrc(flagBaseUrl, c.iso);
                img.addEventListener('error', () => {
                    img.hidden = true;
                });
                const meta = document.createElement('span');
                meta.className = 'quick-apply__phone-option-meta';
                meta.innerHTML = `<span class="quick-apply__phone-option-name">${escapeAttr(countryDisplayName(c.iso, funnelLanguage))}</span>`
                    + `<span class="quick-apply__phone-option-dial">+${escapeAttr(c.dial)}</span>`;
                btn.appendChild(img);
                btn.appendChild(meta);
                btn.addEventListener('click', () => {
                    iso = c.iso;
                    paintCountry();
                    closeOverlay();
                    national.focus();
                });
                li.appendChild(btn);
                list.appendChild(li);
            });
            if (String(query || '').trim() !== '') {
                setHighlight(0);
                return;
            }
            const selectedIdx = rows.findIndex((c) => c.iso === iso);
            setHighlight(selectedIdx >= 0 ? selectedIdx : 0);
        };

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeOverlay();
            }
        });
        overlay.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeOverlay();
                trigger.focus();
                return;
            }
            const buttons = optionButtons();
            if (buttons.length === 0) {
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight(highlighted + 1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight(highlighted - 1);
            } else if (e.key === 'Home') {
                e.preventDefault();
                setHighlight(0);
            } else if (e.key === 'End') {
                e.preventDefault();
                setHighlight(buttons.length - 1);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                buttons[highlighted]?.click();
            }
        });
        search.addEventListener('input', () => renderList(search.value));
        renderList('');
        search.focus();
    };

    trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openOverlay();
    });

    if (flagImg) {
        flagImg.addEventListener('error', () => {
            flagImg.hidden = true;
        });
    }

    return () => {
        closeOverlay();
        national.removeEventListener('input', onNationalInput);
    };
}

